import { useCallback, useEffect, useState } from 'react';
import {
  useMachineStore,
  useUpdateStatus,
  useDownloadProgress,
  useAppVersion,
  useUpdateError,
  useShowJogPadDeco,
} from '@/store/store';
import { CloseIcon, ChevronRightIcon } from '@/icons';
import { friendlyUpdateError } from '@/lib/updateErrors';
import styles from '@/components/SettingsModal/SettingsModal.module.css';

interface SettingsModalProps {
  readonly onClose: () => void;
}

interface SegmentOption<T extends string> {
  readonly label: string;
  readonly value: T;
}

/** Pill segmented control used for UoM and Cooling Mode rows. */
function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  readonly options: readonly SegmentOption<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly ariaLabel: string;
}): JSX.Element {
  return (
    <div className={styles.segmented} role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          className={`${styles.segment} ${value === opt.value ? styles.segmentActive : ''}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Settings modal opened from the header gear icon (or the header Update button).
 * Hosts the auto-update flow: download v{version} → restart & install.
 */
export function SettingsModal({ onClose }: SettingsModalProps): JSX.Element {
  const status = useUpdateStatus();
  const progress = useDownloadProgress();
  const appVersion = useAppVersion();
  const error = useUpdateError();
  const showJogPadDeco = useShowJogPadDeco();

  const setUpdateStatus = useMachineStore((s) => s.setUpdateStatus);
  const setDownloadProgress = useMachineStore((s) => s.setDownloadProgress);
  const setShowJogPadDeco = useMachineStore((s) => s.setShowJogPadDeco);

  const handleDownload = useCallback(() => {
    setUpdateStatus('downloading');
    setDownloadProgress(0);
    window.electronAPI?.updater?.startDownload();
  }, [setUpdateStatus, setDownloadProgress]);

  const [isRestarting, setIsRestarting] = useState(false);

  const handleInstall = useCallback(() => {
    setIsRestarting(true);
    window.electronAPI?.updater?.quitAndInstall();
  }, []);

  // Fullscreen toggle (F11 replacement for the Pi kiosk). Sync the initial state
  // from the main process so the switch reflects the real window state.
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    void window.electronAPI?.windowControls?.isFullscreen().then(setIsFullscreen);
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    void window.electronAPI?.windowControls?.toggleFullscreen().then(setIsFullscreen);
  }, []);

  // UI-only preferences: not yet wired to the store/backend.
  const [lighting, setLighting] = useState(false);
  const [uom, setUom] = useState<'mm' | 'in'>('mm');
  const [coolingMode, setCoolingMode] = useState<'air' | 'oil' | 'air+oil'>('air');

  return (
    <div
      className={styles.settingsOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      onClick={onClose}
    >
      <div className={styles.settingsModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.settingsHeader}>
          <span className={styles.settingsTitle}>Settings</span>
          <button
            className={styles.settingsClose}
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <div className={styles.settingsBody}>
          {/* Software update (mechanism unchanged) */}
          <div className={styles.settingsUpdateBlock}>
            {status === 'idle' && (
              <span className={styles.settingsUpdateText}>Your software is up to date.</span>
            )}

            {status === 'available' && (
              <div className={styles.settingsUpdateInline}>
                <span className={styles.settingsUpdateText}>New system update available</span>
                <button
                  className={`${styles.settingsBtn} ${styles.settingsBtnDownload}`}
                  onClick={handleDownload}
                  type="button"
                >
                  Download
                </button>
              </div>
            )}

            {status === 'downloading' && (
              <>
                <span className={styles.settingsUpdateText}>
                  Downloading… <strong>{progress}%</strong>
                </span>
                <div className={styles.settingsProgressTrack}>
                  <div className={styles.settingsProgressBar} style={{ width: `${progress}%` }} />
                </div>
              </>
            )}

            {status === 'ready' && (
              <div className={styles.settingsUpdateInline}>
                <span className={styles.settingsUpdateText}>Update downloaded.</span>
                <button
                  className={`${styles.settingsBtn} ${styles.settingsBtnDownload}`}
                  onClick={handleInstall}
                  type="button"
                  disabled={isRestarting}
                >
                  {isRestarting ? 'Restarting…' : 'Restart'}
                </button>
              </div>
            )}

            {status === 'error' && (
              <div className={styles.settingsUpdateInline}>
                <span className={styles.settingsUpdateError}>{friendlyUpdateError(error)}</span>
                <button
                  className={`${styles.settingsBtn} ${styles.settingsBtnDownload}`}
                  onClick={handleDownload}
                  type="button"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>

          {/* Preferences */}
          <div className={styles.settingsList}>
            <div className={styles.settingsRow}>
              <span className={styles.settingsRowLabel}>Fullscreen</span>
              <button
                type="button"
                role="switch"
                aria-checked={isFullscreen}
                aria-label="Fullscreen"
                className={`${styles.settingsToggle} ${isFullscreen ? styles.settingsToggleOn : ''}`}
                onClick={handleToggleFullscreen}
              >
                <span className={styles.settingsToggleKnob} />
              </button>
            </div>

            <div className={styles.settingsGroup}>
              <div className={styles.settingsRow}>
                <span className={styles.settingsRowLabel}>Lighting</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={lighting}
                  aria-label="Lighting"
                  className={`${styles.settingsToggle} ${lighting ? styles.settingsToggleOn : ''}`}
                  onClick={() => setLighting(!lighting)}
                >
                  <span className={styles.settingsToggleKnob} />
                </button>
              </div>

              <div className={styles.settingsRow}>
                <span className={styles.settingsRowLabel}>Units of Measure (UoM)</span>
                <SegmentedControl
                  ariaLabel="Units of Measure"
                  value={uom}
                  onChange={setUom}
                  options={[
                    { label: 'Millimeters (mm)', value: 'mm' },
                    { label: 'Inches (in)', value: 'in' },
                  ]}
                />
              </div>

              <div className={styles.settingsRow}>
                <span className={styles.settingsLabelGroup}>
                  <span className={styles.settingsRowLabel}>Enable Diagonal Jog</span>
                  <span className={styles.settingsRowSub}>
                    Allow diagonal movement with jog control
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showJogPadDeco}
                  aria-label="Jog pad decorations"
                  className={`${styles.settingsToggle} ${showJogPadDeco ? styles.settingsToggleOn : ''}`}
                  onClick={() => setShowJogPadDeco(!showJogPadDeco)}
                >
                  <span className={styles.settingsToggleKnob} />
                </button>
              </div>

              <div className={styles.settingsRow}>
                <span className={styles.settingsRowLabel}>Language</span>
                <button type="button" className={styles.settingsLanguageBtn}>
                  <span>English</span>
                  <ChevronRightIcon width={18} height={18} />
                </button>
              </div>
            </div>

            <div className={styles.settingsRow}>
              <span className={styles.settingsRowLabel}>Cooling Mode</span>
              <SegmentedControl
                ariaLabel="Cooling Mode"
                value={coolingMode}
                onChange={setCoolingMode}
                options={[
                  { label: 'Air', value: 'air' },
                  { label: 'Oil', value: 'oil' },
                  { label: 'Air + Oil', value: 'air+oil' },
                ]}
              />
            </div>
          </div>

          <div className={styles.settingsFooter}>
            <span>App Version:</span>
            <strong>Version {appVersion || '—'}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
